import React, { useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { SESSION_STATUSES, selectors, utils } from 'state';
import { isSimplifiedOptionSettlementFailed } from '../../../state/app/simplified_options/presentation';
import CashExchangeForm from './ExecutionWorkspace/CashExchangeForm';
import ODAForm from './ExecutionWorkspace/ODAForm';
import OrdinaryOrderForm from './ExecutionWorkspace/OrdinaryOrderForm';
import SimplifiedOptionOrderForm from './ExecutionWorkspace/SimplifiedOptionOrderForm';
import type { ExecutionIntent } from '../MarketsTabV3/executionModel';
import { executionIntentKey } from '../MarketsTabV3/executionModel';
import {
  ordinaryTradeIsDisabled,
  sessionBlocksTrading,
} from '../MarketsTabV3/model';
import type { MarketInstrument } from '../MarketsTabV3/types';
import SelectedInstrumentDetails from './SelectedInstrumentDetails';
import MarketClosedLabel, { useMarketClosed } from './MarketClosedLabel';

type Props = {
  intent: ExecutionIntent;
  selectedRow?: MarketInstrument;
  reportingCurrencySymbol: string;
  showAvgOpenPrice?: boolean;
  showMarketClosedLabel?: boolean;
  close: () => void;
  completeOrder: () => void;
};

const TITLES = {
  order: 'Order ticket',
  optionOrder: 'Option order',
  oda: 'Order automation',
  exchange: 'Cash exchange',
} as const;

const ExecutionWorkspace: React.FC<Props> = ({
  intent,
  selectedRow,
  reportingCurrencySymbol,
  showAvgOpenPrice = false,
  showMarketClosedLabel = false,
  close,
  completeOrder,
}) => {
  const session = useSelector(selectors.activeSession);
  const assets = useSelector(selectors.underlyingAssets);
  const instruments = useSelector(selectors.financialInstruments);
  const marketHours = useSelector(selectors.marketHours);
  const contentRef = useRef<HTMLDivElement>(null);
  const intentKey = executionIntentKey(intent);

  useLayoutEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [intentKey]);

  const targetInstrument =
    'instrumentId' in intent ? instruments[intent.instrumentId] : undefined;
  const targetAsset = targetInstrument
    ? assets[targetInstrument.underlying_asset]
    : undefined;
  const marketClosed = useMarketClosed({
    asset: targetAsset,
    session,
    marketHours: targetAsset
      ? marketHours[targetAsset.market_hours]
      : undefined,
    enabled: showMarketClosedLabel && intent.kind === 'order',
  });
  const tradingBlocked = sessionBlocksTrading(session.status);
  const invalidTarget =
    'instrumentId' in intent && (!targetInstrument || !targetAsset);
  const targetClosedFromInterval = targetAsset
    ? utils.assetIsOpenNew(
        targetAsset,
        session,
        marketHours[targetAsset.market_hours],
      )[1]
    : true;
  const ticketBlocked = (() => {
    if (tradingBlocked || invalidTarget) return true;
    if (intent.kind === 'order' && targetAsset && targetInstrument) {
      return (
        marketClosed ||
        ordinaryTradeIsDisabled({
          status: session.status,
          asset: targetAsset,
          instrument: targetInstrument,
          closedFromInterval: targetClosedFromInterval,
        })
      );
    }
    if (intent.kind === 'optionOrder') {
      const option = targetInstrument?.simplified_option;
      return (
        option?.availability?.available !== true ||
        isSimplifiedOptionSettlementFailed(option)
      );
    }
    if (intent.kind === 'oda') return !targetInstrument?.amount;
    return false;
  })();

  if (intent.kind === 'idle') {
    return (
      <aside
        className="markets-v3-execution markets-v4-execution is-idle"
        aria-label="Execution desk"
      >
        <div className="markets-v3-execution__head">
          <span>Execution desk</span>
          <h2>Ready when you are</h2>
        </div>
        <div className="markets-v3-execution__idle">
          {tradingBlocked && (
            <div className="markets-v3-execution__blocked">
              <i /> {SESSION_STATUSES[session.status]} · execution unavailable
            </div>
          )}
          <SelectedInstrumentDetails
            row={selectedRow}
            reportingCurrencySymbol={reportingCurrencySymbol}
            showAvgOpenPrice={showAvgOpenPrice}
          />
        </div>
      </aside>
    );
  }

  const title = TITLES[intent.kind];
  const name =
    intent.kind === 'exchange'
      ? 'Exchange cash'
      : targetAsset?.name || targetAsset?.ticker || 'Unavailable instrument';

  return (
    <aside
      className="markets-v3-execution markets-v4-execution is-active"
      aria-label="Execution desk"
    >
      <div className="markets-v3-execution__head">
        <div>
          <span
            className={
              showMarketClosedLabel ? 'markets-v5-ticket-heading' : undefined
            }
          >
            {title}
            <MarketClosedLabel closed={marketClosed} />
          </span>
          <h2>{name}</h2>
        </div>
      </div>
      <div ref={contentRef} className="markets-v3-execution__content">
        <React.Fragment key={intentKey}>
          {invalidTarget && (
            <div className="markets-v3-execution__missing">
              This instrument is no longer available. Close this ticket and
              choose another market.
            </div>
          )}
          {!invalidTarget && intent.kind === 'order' && (
            <div className="markets-v4-order-and-details">
              <OrdinaryOrderForm
                id={intent.instrumentId}
                onComplete={completeOrder}
                tradingBlocked={ticketBlocked}
              />
              <SelectedInstrumentDetails
                row={selectedRow}
                reportingCurrencySymbol={reportingCurrencySymbol}
                showAvgOpenPrice={showAvgOpenPrice}
              />
            </div>
          )}
          {!invalidTarget && intent.kind === 'optionOrder' && (
            <div className="markets-v4-order-and-details">
              <SimplifiedOptionOrderForm
                id={intent.instrumentId}
                onComplete={completeOrder}
                tradingBlocked={ticketBlocked}
              />
              <SelectedInstrumentDetails
                row={selectedRow}
                reportingCurrencySymbol={reportingCurrencySymbol}
                showAvgOpenPrice={showAvgOpenPrice}
              />
            </div>
          )}
          {!invalidTarget && intent.kind === 'oda' && (
            <ODAForm
              id={intent.instrumentId}
              onComplete={close}
              tradingBlocked={ticketBlocked}
            />
          )}
          {intent.kind === 'exchange' && (
            <CashExchangeForm
              onComplete={close}
              tradingBlocked={ticketBlocked}
            />
          )}
        </React.Fragment>
      </div>
    </aside>
  );
};

export default ExecutionWorkspace;
